"use client";

import { VideoTranscriptDialog } from "@/components/educators/components/video-transcript-dialog";
import type { EducatorVideoTranscript } from "@/components/educators/data/video-transcripts";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRef, useState } from "react";

const VIMEO_ORIGIN = "https://player.vimeo.com";

type VimeoPhoneVideoProps = {
  src: string;
  title: string;

  /** Outer wrapper around the phone (often includes blob background). */
  outerClassName?: string;
  /** Phone container (controls rotation/position/sizing). */
  phoneContainerClassName?: string;
  /** Width/max-width utilities for the phone frame. Defaults to full spotlight size. */
  phoneMaxWidthClassName?: string;

  /** Optional badge that sits outside the clipped phone frame. */
  badgeSrc?: string;
  badgeClassName?: string;

  /** Optional spoken transcript — enables subtle “Read transcript” + SEO copy. */
  transcript?: EducatorVideoTranscript;
  transcriptTestId?: string;
};

function updateVimeoParams(src: string, params: Record<string, string>): string {
  // If it's not a Vimeo embed, just return as-is.
  if (!src.includes("player.vimeo.com")) return src;
  try {
    const url = new URL(src);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    // If src isn't a valid URL (shouldn't happen here), don't break rendering.
    return src;
  }
}

export function VimeoPhoneVideo({
  src,
  title,
  outerClassName = "",
  phoneContainerClassName = "",
  phoneMaxWidthClassName = "w-full max-w-[320px]",
  badgeSrc,
  badgeClassName = "",
  transcript,
  transcriptTestId,
}: VimeoPhoneVideoProps) {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isMobile = useIsMobile();

  // On mobile, allow the native Vimeo UI so the user can explicitly play/unmute (required for audio on iOS/Android).
  const effectiveSrc = isMobile
    ? updateVimeoParams(src, {
        background: "0",
        autoplay: "0",
        controls: "1",
        muted: "0",
      })
    : src;

  return (
    <div className={outerClassName}>
      <div
        className={`relative ${phoneMaxWidthClassName} aspect-[9/16] overflow-visible ${phoneContainerClassName}`}
      >
        {/* Border overlay (outside) */}
        <div className="absolute inset-0 rounded-[2.5rem] border-8 border-black pointer-events-none z-20" />

        {/* Phone frame (single element owns border+rounding+clipping to avoid hairline gaps) */}
        <div
          className="w-full h-full rounded-[2.5rem] overflow-hidden relative bg-black"
          style={{ aspectRatio: "9 / 16" }}
        >
          {/* IMPORTANT: On mobile, rely on Vimeo's native click-to-play UI for reliable audio permission. */}
          {!isMobile && (
            <button
              type="button"
              onClick={() => {
                const next = !playing;
                setPlaying(next);
                iframeRef.current?.contentWindow?.postMessage(
                  { method: next ? "play" : "pause" },
                  VIMEO_ORIGIN
                );
              }}
              className="absolute inset-0 z-10 grid place-items-center bg-black/0 hover:bg-black/10 transition-colors group"
              aria-label={playing ? "Pause video" : "Play video"}
            >
              {!playing && (
                <img
                  src="/images/marketing/educators/featured/play.svg"
                  alt=""
                  aria-hidden="true"
                  className="w-20 h-20 transition-transform duration-150 group-hover:scale-110"
                />
              )}
            </button>
          )}
          <iframe
            ref={iframeRef}
            src={effectiveSrc}
            title={title}
            className={`absolute inset-0 w-full h-full ${
              isMobile ? "pointer-events-auto" : "pointer-events-none"
            }`}
            style={{ aspectRatio: "9 / 16" }}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            loading="lazy"
            allowFullScreen
          />

          {transcript ? (
            <div
              className={`absolute z-30 pointer-events-none ${
                isMobile ? "bottom-11 right-5" : "bottom-2 right-5"
              }`}
            >
              <div className="pointer-events-auto">
                <VideoTranscriptDialog
                  transcript={transcript}
                  videoTitle={title}
                  testId={transcriptTestId}
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* Badge is a sibling (so it won't be clipped by the phone frame) */}
        {badgeSrc ? (
          <img src={badgeSrc} alt="" aria-hidden="true" className={badgeClassName} />
        ) : null}
      </div>
    </div>
  );
}
